import React from "react";
import { fetchDelistingNotices } from "../../services/exchangeNotiApi";
import Skeleton from "../common/skeleton";
import EmptyState from "../common/empty_state";

function calcDDay(endDateStr) {
    if (!endDateStr) return null;
    const end = new Date(endDateStr);
    if (isNaN(end.getTime())) return null;
    const now = new Date();
    end.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    const diffMs = end - now;
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function DDayBadge({ endDate }) {
    if (!endDate) {
        return <span className="text-xs text-content-muted">미정</span>;
    }
    const days = calcDDay(endDate);
    if (days === null) {
        return <span className="text-xs text-content-muted">미정</span>;
    }
    if (days < 0) {
        return <span className="text-xs font-semibold text-loss">종료됨</span>;
    }
    return (
        <span className={`text-xs font-semibold ${days <= 7 ? "text-loss" : "text-warning"}`}>
            D-{days}
        </span>
    );
}

class DelistingNoticesPanel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            notices: [],
            loading: true,
            error: false,
            count: 0,
        };
        this._interval = null;
    }

    componentDidMount() {
        this.fetchData();
        this._interval = setInterval(this.fetchData, 30000);
    }

    componentWillUnmount() {
        if (this._interval) {
            clearInterval(this._interval);
        }
    }

    fetchData = () => {
        fetchDelistingNotices({ pages: 5, per_page: 20 })
            .then((res) => {
                const notices = res.data?.notices ?? (Array.isArray(res.data) ? res.data : []);
                this.setState({ notices, count: res.data?.count ?? notices.length, loading: false, error: false });
            })
            .catch(() => {
                this.setState({ error: true, loading: false });
            });
    };

    renderSkeleton() {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                <div className="mb-4 flex items-center gap-3">
                    <Skeleton variant="line" width="8rem" />
                    <Skeleton variant="line" width="3rem" className="ml-auto" />
                </div>
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="mb-3 flex items-center gap-3">
                        <Skeleton variant="line" width="4rem" />
                        <Skeleton variant="line" width="5rem" />
                        <Skeleton variant="line" width="3rem" />
                        <Skeleton variant="line" className="flex-1" />
                    </div>
                ))}
            </div>
        );
    }

    render() {
        const { loading, error, notices, count } = this.state;

        if (loading) return this.renderSkeleton();

        if (error) {
            return (
                <div className="rounded-2xl border border-loss/20 bg-loss/10 p-4 shadow-card sm:p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className="inline-block h-2 w-2 rounded-full bg-loss" />
                            <span className="text-sm font-semibold text-loss">
                                거래지원 종료 공지를 불러올 수 없습니다
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={this.fetchData}
                            className="rounded-lg border border-loss/30 px-3 py-1 text-xs font-medium text-loss hover:bg-loss/10 transition-colors"
                        >
                            재시도
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:p-6">
                {/* 헤더 */}
                <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-content">거래지원 종료 공지</h3>
                    <span className="rounded-full bg-surface-elevated px-2.5 py-1 text-xs font-semibold text-content-secondary">
                        {count}건
                    </span>
                </div>

                {notices.length === 0 ? (
                    <EmptyState
                        title="거래지원 종료 예정 공지가 없습니다"
                        description="현재 거래지원 종료가 예정된 코인이 없습니다."
                    />
                ) : (
                    <>
                        {/* 데스크톱 컬럼 헤더 */}
                        <div className="mb-2 hidden items-center gap-3 border-b border-surface-border pb-2 text-xs font-semibold text-content-secondary sm:flex">
                            <span className="w-14 shrink-0 text-center">티커</span>
                            <span className="w-28 shrink-0">코인명</span>
                            <span className="flex-1">공지 제목</span>
                            <span className="w-24 shrink-0 text-center">종료일</span>
                            <span className="w-16 shrink-0 text-center">D-Day</span>
                        </div>

                        {/* 목록 */}
                        <div className="divide-y divide-surface-border max-h-96 overflow-y-auto">
                            {notices.map((notice, idx) => (
                                <div key={notice.notice_id || idx} className="py-3 first:pt-0 last:pb-0">
                                    {/* 모바일 카드 레이아웃 */}
                                    <div className="flex flex-col gap-1.5 sm:hidden">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-bold text-content">
                                                {notice.coin_name || "-"}
                                            </span>
                                            <span className="rounded bg-surface-elevated px-1.5 py-0.5 text-xs font-medium text-content-secondary">
                                                {notice.symbol || "-"}
                                            </span>
                                            <span className="ml-auto">
                                                <DDayBadge endDate={notice.end_date} />
                                            </span>
                                        </div>
                                        {notice.notice_id ? (
                                            <a
                                                href={`https://upbit.com/service_center/notice?id=${notice.notice_id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-content-muted hover:text-brand-light hover:underline leading-snug"
                                            >
                                                {notice.title}
                                            </a>
                                        ) : (
                                            <span className="text-xs text-content-muted leading-snug">{notice.title}</span>
                                        )}
                                        <span className="text-xs text-content-muted">{notice.end_date || "미정"}</span>
                                    </div>

                                    {/* 데스크톱 테이블 레이아웃 */}
                                    <div className="hidden sm:flex items-center gap-3">
                                        <span className="w-14 shrink-0 text-center">
                                            <span className="rounded bg-surface-elevated px-1.5 py-0.5 text-xs font-semibold text-content-secondary">
                                                {notice.symbol || "-"}
                                            </span>
                                        </span>
                                        <span className="w-28 shrink-0 text-sm font-semibold text-content truncate">
                                            {notice.coin_name || "-"}
                                        </span>
                                        <span className="flex-1 min-w-0">
                                            {notice.notice_id ? (
                                                <a
                                                    href={`https://upbit.com/service_center/notice?id=${notice.notice_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-sm text-content hover:text-brand-light hover:underline truncate block"
                                                >
                                                    {notice.title}
                                                </a>
                                            ) : (
                                                <span className="text-sm text-content truncate block">{notice.title}</span>
                                            )}
                                        </span>
                                        <span className="w-24 shrink-0 text-center text-xs text-content-muted">
                                            {notice.end_date || "미정"}
                                        </span>
                                        <span className="w-16 shrink-0 text-center">
                                            <DDayBadge endDate={notice.end_date} />
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        );
    }
}

export default DelistingNoticesPanel;
